import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { UpgradePrompt } from "../components/UpgradePrompt";
import axios from "axios";
import { Progress } from "../components/ui/progress";
import {
  ArrowLeft,
  Play,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  BookOpen,
  Car,
  Lock,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Section() {
  const { sectionSlug } = useParams();
  const { user, hasActiveSubscription } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState(null);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState([]);
  const [expandedModule, setExpandedModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessStatus, setAccessStatus] = useState(null);

  const isSubscribed = hasActiveSubscription() || user?.role === "admin";

  useEffect(() => {
    fetchSectionData();
  }, [sectionSlug]);

  const fetchSectionData = async () => {
    try {
      const requests = [
        axios.get(`${API_URL}/api/sections/${sectionSlug}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/modules`, { withCredentials: true }),
        axios.get(`${API_URL}/api/lessons`, { withCredentials: true }),
        axios.get(`${API_URL}/api/progress`, { withCredentials: true }),
      ];
      if (!isSubscribed) {
        requests.push(
          axios.get(`${API_URL}/api/lessons/access/status`, { withCredentials: true }).catch(() => ({ data: null }))
        );
      }
      const results = await Promise.all(requests);
      
      setSection(results[0].data);
      const sectionModules = results[1].data.filter(m => m.section_id === results[0].data.id);
      setModules(sectionModules);
      setLessons(results[2].data);
      setProgress(results[3].data);
      if (!isSubscribed && results[4]) {
        setAccessStatus(results[4].data);
      }
      
      if (sectionModules.length > 0) {
        setExpandedModule(sectionModules[0].id);
      }
    } catch (error) {
      console.error("Error fetching section data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getModuleLessons = (moduleId) => {
    return lessons.filter((l) => l.module_id === moduleId);
  };

  const isLessonCompleted = (lessonId) => {
    return progress.some((p) => p.lesson_id === lessonId && p.completed);
  };

  const getLessonStatus = (lessonId) => {
    // Returns "complete" | "in_progress" | "not_started"
    const row = progress.find((p) => p.lesson_id === lessonId);
    if (!row) return "not_started";
    if (row.completed || row.status === "complete") return "complete";
    if (row.status === "in_progress") return "in_progress";
    return "not_started";
  };

  const getModuleProgress = (moduleId) => {
    const moduleLessons = getModuleLessons(moduleId);
    if (moduleLessons.length === 0) return 0;
    const completed = moduleLessons.filter((l) => isLessonCompleted(l.id)).length;
    return Math.round((completed / moduleLessons.length) * 100);
  };

  const getSectionProgress = () => {
    const sectionLessons = modules.flatMap(m => getModuleLessons(m.id));
    if (sectionLessons.length === 0) return 0;
    const completed = sectionLessons.filter((l) => isLessonCompleted(l.id)).length;
    return Math.round((completed / sectionLessons.length) * 100);
  };

  const isLessonAccessible = (lessonId) => {
    if (isSubscribed) return true;
    if (!accessStatus) return true;
    if (accessStatus.is_subscribed) return true;
    // Already accessed lessons are always accessible
    if (accessStatus.accessed_lesson_ids.includes(lessonId)) return true;
    // Under the free limit
    return accessStatus.accessed_count < accessStatus.free_limit;
  };

  // Get global lesson index for ordering (for showing "Free" badge on first 3)
  const allSectionLessons = modules.flatMap(m => getModuleLessons(m.id));
  const getLessonGlobalIndex = (lessonId) => allSectionLessons.findIndex(l => l.id === lessonId);

  const getTotalLessons = () => {
    return modules.reduce((acc, m) => acc + getModuleLessons(m.id).length, 0);
  };

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  if (!section) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 mb-4">Section not found</p>
          <Link to="/dashboard" className="text-blue-600 font-medium">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 pb-6">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/dashboard" className="text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div className="flex-1">
            <h1 className="font-bold text-slate-900">{section.title}</h1>
            <p className="text-sm text-slate-500">{modules.length} modules · {getTotalLessons()} lessons</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Section Description */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <p className="text-slate-600 leading-relaxed">{section.description}</p>
        </div>

        {/* Overall Progress */}
        <div
          data-testid="section-progress-card"
          className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="font-semibold text-slate-900">Section Progress</span>
            <span className="text-lg font-bold text-blue-700">{getSectionProgress()}%</span>
          </div>
          <Progress value={getSectionProgress()} className="h-2 bg-slate-100" />
        </div>

        {/* Modules List */}
        <div className="space-y-4">
          {modules.map((module, moduleIndex) => {
            const moduleLessons = getModuleLessons(module.id);
            const moduleProgress = getModuleProgress(module.id);
            const isExpanded = expandedModule === module.id;
            const isComplete = moduleProgress === 100;

            return (
              <div
                key={module.id}
                data-testid={`section-module-${moduleIndex}`}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm"
              >
                {/* Module Header */}
                <button
                  data-testid={`module-toggle-${moduleIndex}`}
                  onClick={() => setExpandedModule(isExpanded ? null : module.id)}
                  className="w-full p-5 flex items-center gap-4 text-left hover:bg-slate-50 transition-colors"
                >
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                    {module.thumbnail_url ? (
                      <img
                        src={module.thumbnail_url}
                        alt={module.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-slate-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {isComplete && (
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                      )}
                    </div>
                    <p className="font-semibold text-slate-900 mt-1">{module.title}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-slate-500">
                        {moduleLessons.length} lessons
                      </span>
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden max-w-[100px]">
                        <div
                          className={`h-full ${isComplete ? "bg-emerald-500" : "bg-blue-600"} transition-all duration-500`}
                          style={{ width: `${moduleProgress}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-slate-600">{moduleProgress}%</span>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>

                {/* Lessons List */}
                {isExpanded && moduleLessons.length > 0 && (
                  <div className="border-t border-slate-100">
                    {moduleLessons.map((lesson, lessonIndex) => {
                      const completed = isLessonCompleted(lesson.id);
                      const statusKey = getLessonStatus(lesson.id);
                      const accessible = isLessonAccessible(lesson.id);
                      const isPremium = !!lesson.premium;
                      const locked = !accessible || (isPremium && !isSubscribed);
                      return (
                        <div
                          key={lesson.id}
                          data-testid={`lesson-item-${moduleIndex}-${lessonIndex}`}
                          onClick={() => locked ? navigate("/upgrade") : navigate(`/lesson/${lesson.id}`)}
                          className={`flex items-center gap-4 p-4 transition-colors border-b border-slate-100 last:border-b-0 cursor-pointer ${
                            locked ? "hover:bg-blue-50/50" : "hover:bg-slate-50"
                          }`}
                        >
                          <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              locked
                                ? "bg-slate-100"
                                : completed
                                ? "bg-emerald-100"
                                : statusKey === "in_progress"
                                ? "bg-amber-100"
                                : "bg-blue-100"
                            }`}
                          >
                            {locked ? (
                              <Lock className="w-4 h-4 text-slate-400" />
                            ) : completed ? (
                              <CheckCircle className="w-5 h-5 text-emerald-600" />
                            ) : (
                              <Play className={`w-5 h-5 ${statusKey === "in_progress" ? "text-amber-600" : "text-blue-600"}`} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p
                              className={`font-medium ${
                                locked ? "text-slate-400" : completed ? "text-slate-500" : "text-slate-900"
                              }`}
                            >
                              {lesson.title}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              {lesson.duration_minutes && (
                                <p className="text-xs text-slate-500 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {lesson.duration_minutes} min
                                </p>
                              )}
                              {!locked && statusKey !== "not_started" && (
                                <span
                                  data-testid={`status-badge-${lesson.id}`}
                                  className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                                    statusKey === "complete"
                                      ? "bg-emerald-100 text-emerald-700"
                                      : "bg-amber-100 text-amber-700"
                                  }`}
                                >
                                  {statusKey === "complete" ? "Done" : "In progress"}
                                </span>
                              )}
                              {isPremium && (
                                <span
                                  data-testid={`premium-badge-${lesson.id}`}
                                  className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500 text-white"
                                >
                                  Premium
                                </span>
                              )}
                            </div>
                          </div>
                          {locked && (
                            <span className="text-xs text-blue-600 font-medium bg-blue-50 px-2.5 py-1 rounded-full border border-blue-200 flex-shrink-0">
                              Premium
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Empty state */}
                {isExpanded && moduleLessons.length === 0 && (
                  <div className="p-6 text-center border-t border-slate-100">
                    <p className="text-slate-500 text-sm">No lessons added yet</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {modules.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
            <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No modules in this section yet</p>
            <p className="text-sm text-slate-400 mt-2">Content is being added. Check back soon!</p>
          </div>
        )}

        {/* Upgrade prompt for free users */}
        {!isSubscribed && modules.length > 0 && (
          <UpgradePrompt variant="inline" context="Unlock all lessons in this section" />
        )}
      </div>
    </div>
  );
}
